"use client";

import dynamic from "next/dynamic";

import ChartCheckSlot, { ChartCheckLoadingShell } from "@/components/tool-shell/ChartCheckSlot";

import {
  VARSHPHAL_FIELD_IDS,
  VARSHPHAL_FORM,
  VARSHPHAL_FORM_FRAME,
  VARSHPHAL_FORM_RESERVE,
} from "./varshphal-form";
import VarshphalShows from "./VarshphalShows";

// The /varshphal/ chart boundary (the #2460 pattern): the server page imports
// only this module, and it owns the one dynamic() to the island; the shared
// slot (ChartCheckSlot) owns activation, focus hand-off and translate="no".
// The first load also carries the "what the chart shows" block under the
// shell.
//
// Nothing that arrives without a press moves the article below: the slot holds
// two reserved heights, measured with Playwright from 320 to 1280 px wide.
// - The form reserve, from the server HTML (VARSHPHAL_FORM_RESERVE: 890 px on
//   phones, 740 from sm; measured 2026-10-09 on next dev). The form box's
//   floor is its tallest measured state, a three-line location error under the
//   place hint (612 px at 320, 574 at 360, 482 from 640), so the hint or an
//   error fills the room above the action; the "what the chart shows" block
//   (245 px at 320) sits under it. This covers the shell -> form swap after
//   the async chunk loads.
// - The verdict reserve, added by the island inside the submit itself, before
//   the request goes out (VARSHPHAL_VERDICT_RESERVE: 1840 px on phones, 1630
//   from sm, 1060 from md). Re-measured 2026-10-09 on a production build (next
//   build, next start) with POST /varshphal mocked by four replies: the worst
//   case (the longest zone name, "Sept" dates, Vrishchika everywhere a sign is
//   spelled out, all four birth-time flips), a two-flip reply with that zone,
//   the longest strings (a Dhanu lagna, the Muntha's lord Mercury in the 12th
//   house, age 96, four flips) and the contract's Delhi example with a
//   two-flip next varsha. In every state (the verdict; a retrograde graha
//   selected; the next varsha pending, failed, or shown with that graha still
//   selected; back to this year; the South chart), the tallest verdict is 1817
//   at 320, 1814 at 360, 1742-1771 at 390-639, 1609 at 640-700, 1571 at 767,
//   1040 at 768, 1007 at 800 and 975 from 900. A failed next varsha adds no
//   height: its error fills a row the verdict always lays out (VarshphalVerdict).
//   The growth happens in the press that sends the request, an input-driven
//   change (hadRecentInput), not a layout shift; the reply, an error, the next
//   varsha and the North/South toggle all land inside it, and only "Check
//   another birth" releases it. A static verdict reserve would have left about
//   1,000 px of blank under the form on phones for every reader who never
//   casts a chart.
// With those reserves, a PerformanceObserver over the whole flow (every reply
// above, the next varsha answering or failing 1.5 s after its press) saw the
// article below never move after the submit press, and no unexcused shift when
// the next varsha fails. The swap to the next varsha's chart itself redraws the
// chart and its table in place (up to 0.005 inside the verdict, a second after
// the press).
const SHELL_PROPS = { ...VARSHPHAL_FORM, className: VARSHPHAL_FORM_FRAME } as const;

const VarshphalCheck = dynamic(() => import("./VarshphalCheck"), {
  ssr: false,
  loading: () => (
    <>
      <ChartCheckLoadingShell fallback={SHELL_PROPS} />
      <VarshphalShows />
    </>
  ),
});

export default function VarshphalCheckLazy() {
  return (
    <ChartCheckSlot
      island={VarshphalCheck}
      islandProps={{}}
      shell={SHELL_PROPS}
      fieldIds={VARSHPHAL_FIELD_IDS}
      className={`flex flex-col ${VARSHPHAL_FORM_RESERVE}`}
      data={{ "data-varshphal-slot": "" }}
      afterShell={<VarshphalShows />}
    />
  );
}
